import React, { useEffect, useState } from "react";
import { Button, Spinner, Alert } from "reactstrap";
import { Modal } from "antd";
import { ReactTyped } from "react-typed";
import { FormProvider, useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import importApis from "../../api/WorkOrderManagement/ImportApis";
import { Icon } from "../Component";
import FormInput from "../../pages/form-components/InputComponent";
import { useNavigate } from "react-router-dom";

const blobUrl: String = process.env.REACT_APP_BLOB_URL || "";
const blobContainer: String = process.env.REACT_APP_BLOB_CONTAINER || "";

interface PaymentBulkUploadProps {
  onUploadSuccess?: () => void;
  showAsButton?: boolean;
  flowType?: "Contractor" | "Client"; // "Contractor" for payments, "Client" for receivables
}

const PaymentBulkUpload: React.FC<PaymentBulkUploadProps> = ({
  onUploadSuccess,
  showAsButton = false,
  flowType = "Contractor", // Default to "Contractor" for backward compatibility
}) => {
  const [loading, setLoading] = useState(false);
  const [blockEdit, setBlockEdit] = useState(false);
  const [response, setResponse] = useState({
    status: "",
    message: "",
  });
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const files = watch("attachment");
  const [downloadingRef, setDownloadingRef] = useState(false);

  useEffect(() => {
    setResponse({ status: "", message: "" });
  }, [files]);

  const handleDownloadReferenceData = async () => {
    try {
      setDownloadingRef(true);

      // Set report name based on flowType
      const reportName =
        flowType === "Client" ? "Client Receivables" : "Contractor Payments";

      const res: any = await importApis.uploadDefaultData(reportName, flowType);

      if (res?.status && res?.data) {
        // Create a new workbook
        const workbook = XLSX.utils.book_new();

        // Iterate through each array in data and create sheets
        Object.keys(res.data).forEach((key) => {
          const dataArray = res.data[key];

          if (Array.isArray(dataArray) && dataArray.length > 0) {
            // Convert array to worksheet
            const worksheet = XLSX.utils.json_to_sheet(dataArray);

            // Add worksheet to workbook with sheet name as the key
            XLSX.utils.book_append_sheet(workbook, worksheet, key);
          }
        });

        // Generate Excel file and trigger download
        XLSX.writeFile(workbook, "Payment_Reference_Data.xlsx");
      } else {
        setResponse({
          status: "error",
          message: res.message,
        });
      }
    } catch (error) {
      console.error("Error downloading reference data:", error);
      setResponse({
        status: "error",
        message: "An error occurred while downloading reference data",
      });
    } finally {
      setDownloadingRef(false);
    }
  };

  const handleUpload = async () => {
    try {
      const attachment = files;
      if (!attachment || !attachment[0]) {
        setResponse({
          status: "error",
          message: "Please select a file",
        });
        setBlockEdit(false);
        setLoading(false);
        return;
      }
      uploadFile(attachment, setResponse, "File Uploaded Successfully");
    } catch (error) {
      console.log("Upload error:", error);
    }
  };

  const uploadFile = async (
    attachment: any,
    setResponse: any,
    successResponse: any
  ) => {
    try {
      const _file = attachment?.[0];
      const formData: any = new FormData();
      formData.append("files", _file);

      // Pass flowType in request body as JSON object
      // "Contractor" for payments, "Client" for receivables
      const bodyObject = {
        flowType: flowType,
      };
      formData.append("body", JSON.stringify(bodyObject));

      // Call the API for Payment bulk upload
      const res: any = await importApis.uploadPaymentBulk(formData);

      console.log("Bulk upload API response:", res);

      const { status, error, message, data } = res || {};

      if (status) {
        setResponse({
          status: "success",
          message: successResponse + ". Your file is being processed.",
        });

        // Callback for parent component
        if (onUploadSuccess) {
          onUploadSuccess();
        }

        setBlockEdit(false);
        setLoading(false);

        return;
      }

      setResponse({
        status: "error",
        message: error + ": " + message,
      });
      setBlockEdit(false);
      setLoading(false);
    } catch (error) {
      console.log("Upload file error:", error);
      setResponse({
        status: "error",
        message: "An error occurred while uploading the file",
      });
      setBlockEdit(false);
      setLoading(false);
    }
  };

  const isClientFlow = flowType === "Client";
  const uploadTypeLabel = isClientFlow ? "Receivables" : "Payments";
  const partyLabel = isClientFlow ? "clients" : "contractors";

  // Dynamic template URL based on flowType
  const templateFileName = isClientFlow
    ? "RABillPaymentClientBulkUpload.xlsx"
    : "RABillPaymentBulkUpload.xlsx";

  const TriggerButton = showAsButton ? (
    <Button
      color="primary"
      onClick={() => setShowModal(true)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <Icon name="upload-cloud"></Icon>
      <span>Bulk Upload {uploadTypeLabel}</span>
    </Button>
  ) : (
    <a
      onClick={() => {
        setShowModal(true);
      }}
      style={{
        cursor: "pointer",
      }}
    >
      <Icon name="upload-cloud"></Icon>
      <span>Bulk Upload {uploadTypeLabel}</span>
    </a>
  );

  return (
    <>
      {TriggerButton}

      <Modal
        title={`Bulk ${uploadTypeLabel} Upload`}
        open={showModal}
        footer={null}
        onCancel={() => {
          setShowModal(false);
          setBlockEdit(false);
          setValue("attachment", null);
          setLoading(false);
          setResponse(() => ({
            status: "",
            message: "",
          }));
        }}
        width={"60%"}
      >
        <FormProvider {...methods}>
          <form>
            <p>
              You're about to upload multiple {uploadTypeLabel} at once. To
              ensure a smooth import, please{" "}
              <a
                href={`${blobUrl}${blobContainer}/${templateFileName}`}
                target="_self"
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Download Template
              </a>{" "}
              and fill in the required {isClientFlow ? "Receivable" : "Payment"}{" "}
              data accurately and completely, then upload the file below. You
              can also{" "}
              <a
                onClick={handleDownloadReferenceData}
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: downloadingRef ? "#999" : "#1890ff",
                  pointerEvents: downloadingRef ? "none" : "auto",
                }}
              >
                {downloadingRef
                  ? "Downloading Reference Data..."
                  : "Download Reference Data"}
              </a>{" "}
              to get sites and {partyLabel} information.
            </p>

            <div className="text-right mt-3"></div>
            <p>
              <strong>Note: </strong>
              All fields in the template are mandatory. Each record must have
              unique information to avoid errors during the import process. The
              file will be validated asynchronously and you can track the
              progress in the Imports page.
            </p>
            <div style={{ maxWidth: "400px" }}>
              {files && files[0]?.name && (
                <div className="form-group m-0">
                  <div className="form-label-wrapper">
                    <label className="form-label" htmlFor={"attachment"}>
                      File Upload Area
                    </label>
                  </div>
                  <div
                    className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                    style={{ borderRadius: "3px" }}
                  >
                    <span>{files && files[0]?.name}</span>
                    {!blockEdit && (
                      <span
                        onClick={() => {
                          setValue("attachment", null);
                        }}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          display: "block",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross"></Icon> &nbsp;
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div
                style={{
                  display: `${files && files[0]?.name ? "none" : "block"}`,
                }}
              >
                <FormInput
                  type="file"
                  name="attachment"
                  label="File Upload Area"
                  placeholder="File Upload Area"
                  accept=".xlsx"
                />
              </div>
            </div>

            <Alert
              isOpen={response?.status === "error"}
              color="danger"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <ReactTyped
                strings={[response?.message]}
                typeSpeed={30}
                showCursor={false}
              />
            </Alert>

            <Alert
              isOpen={response?.status === "success"}
              color="success"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <p style={{ margin: 0 }}>
                {response?.message}{" "}
                <a
                  onClick={() => {
                    setShowModal(false);
                    setBlockEdit(false);
                    setValue("attachment", null);
                    setLoading(false);
                    setResponse(() => ({
                      status: "",
                      message: "",
                    }));
                    navigate(`${process.env.PUBLIC_URL}/imports-list`);
                  }}
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  View progress in Imports page
                </a>
              </p>
            </Alert>

            <div className="mt-4">
              {response?.status == "success" ? (
                <></>
              ) : (
                <ul className="actions clearfix d-flex">
                  <li>
                    {loading ? (
                      <Button color="primary" type="button" size="md">
                        <Spinner size="sm" color="light" />
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        type="button"
                        disabled={!files?.[0]}
                        onClick={() => {
                          setResponse(() => ({
                            status: "",
                            message: "",
                          }));
                          setBlockEdit(true);
                          setLoading(true);
                          handleUpload();
                        }}
                      >
                        Upload
                      </Button>
                    )}
                  </li>
                  {!loading && (
                    <li className="m-3 mt-0">
                      <Button
                        color="primary"
                        onClick={() => {
                          setShowModal(false);
                          setValue("attachment", null);
                        }}
                        className="btn-dim btn btn-secondary"
                      >
                        Cancel
                      </Button>
                    </li>
                  )}
                </ul>
              )}
            </div>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default PaymentBulkUpload;
